import { ACCOUNT_TYPES, ONBOARDING_STATUS } from "./profileService";

export const ACCOUNT_TYPE_PATH = "/account-type";
export const BUSINESS_HUB_PATH = "/business-hub";
export const BUSINESS_INVITATIONS_PATH = "/business-invitations";
export const PLAYLISTS_PATH = "/playlists";
export const TITLES_PATH = "/titles";
export const BUSINESS_HUB_ACTIVATION_REASON = "activate-business";

/**
 * Where sign-up should return the user afterwards.
 *
 * The TV pairing flow depends on this: a user who starts at /pair, signs up, and
 * finishes onboarding must land back on /pair so the "set up TV" assignment
 * dialog runs and a tvDevices row is written. That context used to travel only
 * in React Router's `location.state`, which is in-memory history state — it does
 * not survive a reload, a restored tab, or a direct visit to /signup. When it
 * was lost the user was routed to /business-hub?next=%2F, isPairReturnFlow was
 * false, the dialog never opened, and the TV paired with no row to manage.
 *
 * The URL is therefore the durable carrier and router state is only a
 * convenience. Relative paths only — an absolute or protocol-relative `next`
 * would make this an open redirect.
 */
export const resolveSignUpReturnPath = ({ stateFrom, search } = {}) => {
  const statePathname = String(stateFrom?.pathname || "").trim();
  if (statePathname) {
    return `${statePathname}${stateFrom?.search || ""}`;
  }

  let next = "";
  try {
    next = String(
      new URLSearchParams(String(search || "")).get("next") || "",
    ).trim();
  } catch {
    next = "";
  }

  if (!next.startsWith("/") || next.startsWith("//")) return "/";
  return next;
};

export const buildBusinessHubActivationPath = (requestedPath = "/") => {
  const next = String(requestedPath || "/").trim() || "/";
  const params = new URLSearchParams();
  params.set("next", next);
  params.set("reason", BUSINESS_HUB_ACTIVATION_REASON);
  return `${BUSINESS_HUB_PATH}?${params.toString()}`;
};

/**
 * Decide whether business-scoped features (analytics, raffles, coupons, capsules, campaign data on
 * the home page) are available, and if not, why — so the UI can show one consistent
 * create/activate/upgrade prompt instead of empty pages or silent redirects.
 *
 * Admins are handled by the caller (they bypass the gate entirely). Returns:
 *   - allowed: true when there is a live (active-status) business context
 *   - reason:  'activate' (draft business), 'create' (business account, none yet),
 *              'upgrade' (individual account), or null when allowed
 */
export const resolveBusinessGate = ({
  accountType,
  hasActiveBusinessContext,
  isActiveBusinessProfileLive,
} = {}) => {
  if (hasActiveBusinessContext && isActiveBusinessProfileLive) {
    return { allowed: true, reason: null };
  }
  if (hasActiveBusinessContext && !isActiveBusinessProfileLive) {
    return { allowed: false, reason: "activate" };
  }
  if (accountType === ACCOUNT_TYPES.INDIVIDUAL) {
    return { allowed: false, reason: "upgrade" };
  }
  return { allowed: false, reason: "create" };
};

/** Resolve the destination + button label for a business-gate reason, preserving a return path. */
export const buildBusinessGateCta = (reason, nextPath = "/") => {
  const next = String(nextPath || "/").trim() || "/";
  if (reason === "activate") {
    return { path: buildBusinessHubActivationPath(next), label: "Activate your business" };
  }
  const path = `${BUSINESS_HUB_PATH}?next=${encodeURIComponent(next)}`;
  if (reason === "upgrade") {
    return { path, label: "Become a business" };
  }
  return { path, label: "Go to Business Hub" };
};

const hasAdminRole = (profile = {}) => {
  if (!profile || typeof profile !== "object") return false;
  if (profile.isAdmin === true || profile.admin === true) return true;

  const role = String(profile.role || "").trim().toLowerCase();
  if (role === "admin") return true;

  if (Array.isArray(profile.roles)) {
    return profile.roles.some((entry) => String(entry || "").trim().toLowerCase() === "admin");
  }

  return false;
};

export const getPostAuthDestination = (profile, fallback = "/", options = {}) => {
  const next = fallback || "/";
  const encodedNext = encodeURIComponent(next);
  const isAdmin = Boolean(options?.isAdmin) || hasAdminRole(profile);

  if (isAdmin) {
    return next;
  }

  // Following an invitation link lands the user on the invitations page
  // regardless of account type. Accepting an invite never requires choosing
  // business vs individual — a new user is an individual (a collaborator) until
  // they decide otherwise, and existing accounts already have a type — so skip
  // the account-type and business-onboarding gates for this destination.
  if (String(next).split("?")[0] === BUSINESS_INVITATIONS_PATH) {
    return next;
  }

  if (!Object.values(ACCOUNT_TYPES).includes(profile?.accountType)) {
    return `${ACCOUNT_TYPE_PATH}?next=${encodedNext}`;
  }

  if (
    profile.accountType === ACCOUNT_TYPES.BUSINESS &&
    [
      ONBOARDING_STATUS.NOT_STARTED,
      ONBOARDING_STATUS.IN_PROGRESS,
    ].includes(profile.onboardingStatus)
  ) {
    return `${BUSINESS_HUB_PATH}?next=${encodedNext}`;
  }

  return next;
};
